import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import MysteryBox from '../components/MysteryBox';
import EnhancedCard from '../components/EnhancedCard';
import ShareModal from '../components/ShareModal';
import PackOpeningAnimation from '../components/PackOpeningAnimation';
import LoginScreen from '../components/LoginScreen';
import LanguageSelector from '../components/LanguageSelector';
import DailyRewardModal from '../components/DailyRewardModal';
import DailyRewardCountdown from '../components/DailyRewardCountdown';
import WalletTrustBadge from '../components/WalletTrustBadge';
import ProminentWalletDisplay from '../components/ProminentWalletDisplay';
import SolanaWalletManager from '../components/SolanaWalletManager';
import OnboardingTutorial from '../components/OnboardingTutorial';
import WelcomeOverlay from '../components/WelcomeOverlay';
import AdminDebugMenu, { useLongPress } from '../components/AdminDebugMenu';
import { Button } from '../components/ui/button';
import { Sparkles, RotateCcw, Images, Share2, Swords, ShoppingBag, Coins, Trophy, Store, Wallet, Shield, Gift, Globe, X, Skull } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../providers/AuthProvider';
import { useI18n } from '../providers/I18nProvider';
import { useGameWallet } from '../providers/GameWalletProvider';
import { saveCard, cleanupStorage } from '../utils/inventoryManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const navigate = useNavigate();
  const { user, walletAddress, isAuthenticated, refreshUser } = useAuth();
  const { t, language, setLanguage } = useI18n();
  const { credits, refreshCredits } = useGameWallet();
  const [isGenerating, setIsGenerating] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const [pulledFigure, setPulledFigure] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showPackAnimation, setShowPackAnimation] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [isPregenerated, setIsPregenerated] = useState(false);
  const [cacheCount, setCacheCount] = useState(0);
  const [showSolanaWallet, setShowSolanaWallet] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showAdminMenu, setShowAdminMenu] = useState(false);
  
  // Daily Reward State
  const [showDailyReward, setShowDailyReward] = useState(false);
  const [dailyRewardAvailable, setDailyRewardAvailable] = useState(false);
  const [dailyRewardSecondsRemaining, setDailyRewardSecondsRemaining] = useState(0);
  const [dailyReward, setDailyReward] = useState(null);
  const [dailyStreak, setDailyStreak] = useState(0);
  const [isClaimingDaily, setIsClaimingDaily] = useState(false);
  const [dailyChecked, setDailyChecked] = useState(false);

  const sessionId = walletAddress || 'default_session';

  // Admin Menu - Long Press on Logo
  const adminLongPress = useLongPress(() => {
    setShowAdminMenu(true);
    toast.info('🔧 Admin Menu Opened');
  }, 3000);

  // FIX: Cleanup storage on first load to prevent QuotaExceededError
  useEffect(() => {
    cleanupStorage();
  }, []);

  // FIX 1: Show Welcome Overlay if no wallet created yet
  useEffect(() => {
    const walletCreated = localStorage.getItem('wallet_created');
    
    // Show welcome overlay IMMEDIATELY if no wallet
    if (!walletCreated && !isAuthenticated) {
      setShowWelcome(true);
      // Don't show tutorial if showing welcome
      setShowTutorial(false);
    }
  }, [isAuthenticated]);

  // Check if user needs onboarding tutorial (after wallet is created)
  useEffect(() => {
    const tutorialCompleted = localStorage.getItem('tutorial_completed');
    const walletCreated = localStorage.getItem('wallet_created');
    
    // Only show tutorial if:
    // 1. Tutorial not completed
    // 2. Wallet IS created (welcome overlay already shown)
    // 3. Welcome overlay is NOT showing
    if (!tutorialCompleted && walletCreated && !showWelcome) {
      // Delay tutorial to let page load
      const timer = setTimeout(() => setShowTutorial(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [showWelcome]);

  // Check daily reward status on app load
  const checkDailyRewardStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/user/daily-reward/status?session_id=${sessionId}`);
      const { available, seconds_remaining, streak } = response.data;
      
      setDailyRewardAvailable(available);
      setDailyRewardSecondsRemaining(seconds_remaining || 0);
      setDailyStreak(streak || 0);
      
      // Auto-show modal if daily reward is available (first thing user sees)
      if (available && !dailyChecked) {
        setShowDailyReward(true);
      }
      
      setDailyChecked(true);
    } catch (error) {
      console.error('Error checking daily reward:', error);
      setDailyChecked(true);
    }
  }, [sessionId, dailyChecked]);

  // Claim daily reward
  const claimDailyReward = useCallback(async () => {
    setIsClaimingDaily(true);
    try {
      const response = await axios.post(`${API}/user/daily-reward/claim?session_id=${sessionId}`);
      const { reward, streak } = response.data;
      
      setDailyReward(reward);
      setDailyStreak(streak);
      setDailyRewardAvailable(false);
      
      // Refresh user data to update shards if needed
      if (refreshUser) {
        await refreshUser();
      }
      
      toast.success('Daily reward claimed!', {
        description: reward.type === 'card' 
          ? `You got a ${reward.rarity} card!`
          : `You got ${reward.amount} shards!`
      });
      
    } catch (error) {
      console.error('Error claiming daily reward:', error);
      toast.error('Failed to claim daily reward');
      setShowDailyReward(false);
    } finally {
      setIsClaimingDaily(false);
    }
  }, [sessionId, refreshUser]);

  // Close daily reward modal and reset
  const closeDailyRewardModal = useCallback(() => {
    setShowDailyReward(false);
    setDailyReward(null);
    // Re-check status to update countdown
    checkDailyRewardStatus();
  }, [checkDailyRewardStatus]);

  useEffect(() => {
    const initialize = async () => {
      try {
        // Check daily reward status first (most important!)
        await checkDailyRewardStatus();
        
        // Check cache
        const cacheResponse = await axios.get(`${API}/figures/cache/count`);
        const currentCount = cacheResponse.data.cached_count;
        setCacheCount(currentCount);
        
        if (currentCount < 3) {
          toast.info('Preparing your mystery boxes...', { duration: 2000 });
          axios.post(`${API}/figures/pregenerate?count=5`).then(() => {
            setIsPregenerated(true);
            setCacheCount(5);
            toast.success('Mystery boxes ready!', { duration: 2000 });
          }).catch(err => {
            console.error('Pre-generation error:', err);
            setIsPregenerated(true);
          });
        } else {
          setIsPregenerated(true);
        }
      } catch (error) {
        console.error('Error initializing:', error);
        setIsPregenerated(true);
      }
    };

    initialize();
  }, [checkDailyRewardStatus]);

  const handleOpenBox = async () => {
    if (isGenerating) return;
    
    // Use wallet address if connected, otherwise default session
    const sessionId = walletAddress || 'default_session';

    setIsShaking(true);
    setIsGenerating(true);
    setPulledFigure(null);
    setShowResult(false);

    try {
      // Short shake animation before API call
      await new Promise(resolve => setTimeout(resolve, 500));

      const response = await axios.post(`${API}/figures/pull`, {
        session_id: sessionId,
      });

      setPulledFigure(response.data);
      setIsShaking(false);
      
      // Show the pack opening animation
      setShowPackAnimation(true);
      
      // Refresh user data if authenticated
      if (isAuthenticated) {
        refreshUser();
      }
      
      const cacheResponse = await axios.get(`${API}/figures/cache/count`);
      setCacheCount(cacheResponse.data.cached_count);

      if (cacheResponse.data.cached_count < 2) {
        axios.post(`${API}/figures/pregenerate?count=3`).catch(err => {
          console.error('Background pre-generation error:', err);
        });
      }
    } catch (error) {
      console.error('Error generating figure:', error);
      toast.error('Failed to generate figure', {
        description: error.response?.data?.detail || 'Please try again',
      });
      setIsShaking(false);
    } finally {
      setIsGenerating(false);
    }
  };
  
  const handlePackAnimationComplete = () => {
    setShowPackAnimation(false);
    setShowResult(true);
    toast.success(`You pulled a ${pulledFigure.rarity} card!`, {
      description: `${pulledFigure.character_name} - ${pulledFigure.title} (${pulledFigure.power_rating}★)`,
    });
    
    // FIX: Use inventoryManager to save card (no image data - prevents QuotaExceededError)
    if (pulledFigure) {
      saveCard(pulledFigure);
      console.log('[Home] Card saved via inventoryManager:', pulledFigure.id || pulledFigure.character_name);
    }
    
    // Refresh user data to sync inventory across ALL pages
    if (refreshUser) {
      refreshUser();
    }
  };

  const handleReset = () => {
    setShowResult(false);
    setPulledFigure(null);
    setIsShaking(false);
  };

  const handleShare = () => {
    setShowShareModal(true);
  };

  const goToGallery = () => {
    navigate('/gallery');
  };

  const goToPvP = () => {
    navigate('/pvp');
  };
  
  const goToShop = () => {
    navigate('/shop');
  };
  
  const goToMarketplace = () => {
    navigate('/marketplace');
  };
  
  const goToLeaderboard = () => {
    navigate('/leaderboard');
  };

  const goToWallet = () => {
    navigate('/wallet');
  };

  const goToSquidGame = () => {
    navigate('/squid-game');
  };

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden pb-24">
      {/* FIX 1: WELCOME OVERLAY - Full Screen, Cannot Miss */}
      <AnimatePresence>
        {showWelcome && (
          <WelcomeOverlay
            onWalletCreated={() => {
              setShowWelcome(false);
              localStorage.setItem('tutorial_completed', 'true');
              toast.success('Welcome to SLAP BATTLES!');
            }}
            onClose={() => {
              setShowWelcome(false);
              localStorage.setItem('wallet_created', 'true');
            }}
          />
        )}
      </AnimatePresence>

      {/* Pack Opening Animation Overlay */}
      <AnimatePresence>
        {showPackAnimation && pulledFigure && (
          <div onClick={handlePackAnimationComplete}>
            <PackOpeningAnimation 
              card={pulledFigure} 
              onComplete={handlePackAnimationComplete} 
            />
          </div>
        )}
      </AnimatePresence>
      
      {/* Cyberpunk grid background */}
      <div 
        className="fixed inset-0 opacity-10 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(#FF00FF15 1px, transparent 1px),
            linear-gradient(90deg, #00FFFF15 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
        }}
      />

      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-neon-pink/10 rounded-full blur-[150px] pointer-events-none" />
      <div className="fixed bottom-0 right-0 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-[150px] pointer-events-none" />

      {/* CLEAN TOP BAR - Wallet as CENTERPIECE */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-b border-yellow-500/30">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 py-2 flex flex-wrap items-center justify-between gap-2">
          {/* Logo - Compact - LONG PRESS FOR ADMIN MENU */}
          <motion.h1
            className="font-anton text-base sm:text-xl md:text-2xl uppercase tracking-tight shrink-0 order-1 cursor-pointer select-none"
            style={{
              background: 'linear-gradient(90deg, #FF00FF 0%, #00FF99 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
            data-testid="app-title"
            {...adminLongPress}
            title="Long press for admin menu"
          >
            <span className="hidden sm:inline">SLAP BATTLES</span>
            <span className="sm:hidden">DT</span>
          </motion.h1>
          
          {/* CENTER: PROMINENT WALLET DISPLAY - THE BIGGEST, MOST OBVIOUS ELEMENT */}
          <div className="flex-1 flex justify-center order-3 sm:order-2 w-full sm:w-auto mt-2 sm:mt-0">
            <ProminentWalletDisplay 
              onOpenSolanaWallet={() => setShowSolanaWallet(true)}
            />
          </div>
          
          {/* Right side: Daily Bonus (Icon only) + Language + Menu */}
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0 order-2 sm:order-3">
            {/* Daily Reward - COMPACT ICON with red dot */}
            <DailyRewardCountdown
              secondsRemaining={dailyRewardSecondsRemaining}
              available={dailyRewardAvailable}
              onClick={() => setShowDailyReward(true)}
            />
            
            {/* FIX 3: Language Toggle - ALWAYS VISIBLE */}
            <motion.button
              onClick={() => setLanguage(language === 'en' ? 'de' : 'en')}
              className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
              whileTap={{ scale: 0.95 }}
              title={language === 'en' ? 'Deutsch' : 'English'}
              data-testid="language-toggle"
            >
              <Globe className="w-4 h-4 sm:w-5 sm:h-5 text-white/60" />
            </motion.button>
            
            {isAuthenticated ? (
              <WalletTrustBadge compact={true} />
            ) : (
              <Button 
                onClick={() => setShowLogin(true)}
                className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-500 hover:to-cyan-500 rounded-lg font-space text-xs py-2 px-2 sm:px-3"
              >
                <Wallet className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">{t('home.connect_wallet')}</span>
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section - with top padding for fixed header */}
      <div className="pt-24 pb-8">
        <motion.div
          className="text-center px-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 
            className="font-anton text-5xl md:text-7xl uppercase tracking-tight"
            style={{
              background: 'linear-gradient(180deg, #FFFFFF 0%, #FF00FF 50%, #00FF99 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            THE MIDNIGHT ARCADE
          </h2>
          <p className="font-space text-sm md:text-base text-white/60 mt-2 tracking-wider">
            LIVE DYNAMIC TCG • BATTLE • TRADE • COLLECT
          </p>
        </motion.div>
      </div>

      <main className="relative z-10 flex flex-col items-center justify-center min-h-[calc(100vh-200px)] px-4">
        <AnimatePresence mode="wait">
          {!showResult ? (
            <motion.div
              key="box"
              className="flex flex-col items-center gap-8"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.3 }}
            >
              <MysteryBox isShaking={isShaking} onClick={handleOpenBox} />

              <Button
                onClick={handleOpenBox}
                disabled={isGenerating}
                className="bg-neon-pink hover:bg-neon-pink/80 text-white font-anton text-xl uppercase px-8 py-6 rounded-full shadow-[0_0_30px_rgba(255,0,255,0.5)] hover:shadow-[0_0_60px_rgba(255,0,255,0.8)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="open-box-button"
              >
                {isGenerating ? (
                  <>
                    <RotateCcw className="w-6 h-6 mr-2 animate-spin" />
                    Opening...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-6 h-6 mr-2" />
                    Open Mystery Box
                  </>
                )}
              </Button>

              <p className="font-space text-sm text-white/40 text-center max-w-md">
                Pull random Degen Cards with dynamic power stats! Trade in PvP Arena!
              </p>
            </motion.div>
          ) : (
            <motion.div
              key="result"
              className="flex flex-col items-center gap-8 max-w-md w-full"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <motion.div
                className="fixed inset-0 bg-white z-[100] pointer-events-none"
                initial={{ opacity: 1 }}
                animate={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              />

              {pulledFigure && <EnhancedCard card={pulledFigure} showNews={true} />}

              <div className="flex gap-4 w-full">
                <Button
                  onClick={handleReset}
                  className="flex-1 bg-transparent border-2 border-white/20 text-white hover:border-white hover:bg-white/10 font-space uppercase"
                  data-testid="pull-again-button"
                >
                  <RotateCcw className="w-5 h-5 mr-2" />
                  Pull Again
                </Button>

                <Button
                  onClick={handleShare}
                  className="flex-1 bg-neon-green hover:bg-neon-green/80 text-black font-space uppercase"
                  data-testid="share-button"
                >
                  <Share2 className="w-5 h-5 mr-2" />
                  Share
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {pulledFigure && (
        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          figure={pulledFigure}
        />
      )}
      
      {/* Login Modal */}
      <AnimatePresence>
        {showLogin && !isAuthenticated && (
          <LoginScreen onClose={() => setShowLogin(false)} />
        )}
      </AnimatePresence>

      {/* STICKY BOTTOM NAVIGATION BAR - 5 ITEMS */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-lg border-t border-white/10 safe-area-pb">
        <div className="max-w-lg mx-auto px-2">
          <div className="flex items-center justify-around py-2">
            {/* Shop */}
            <motion.button
              onClick={goToShop}
              className="flex flex-col items-center gap-0.5 px-2 sm:px-3 py-2 rounded-lg hover:bg-white/10 transition-colors"
              whileTap={{ scale: 0.95 }}
              data-testid="nav-shop"
            >
              <ShoppingBag className="w-5 h-5 sm:w-6 sm:h-6 text-pink-400" />
              <span className="text-[9px] sm:text-[10px] font-space text-white/70 uppercase">Shop</span>
            </motion.button>
            
            {/* Wallet - NEW */}
            <motion.button
              onClick={goToWallet}
              className="flex flex-col items-center gap-0.5 px-2 sm:px-3 py-2 rounded-lg hover:bg-white/10 transition-colors"
              whileTap={{ scale: 0.95 }}
              data-testid="nav-wallet"
            >
              <Wallet className="w-5 h-5 sm:w-6 sm:h-6 text-green-400" />
              <span className="text-[9px] sm:text-[10px] font-space text-white/70 uppercase">Wallet</span>
            </motion.button>
            
            {/* Play (PvP) - Highlighted */}
            <motion.button
              onClick={goToPvP}
              className="flex flex-col items-center gap-0.5 px-3 sm:px-4 py-2 rounded-xl bg-gradient-to-t from-red-600 to-orange-500 shadow-lg shadow-red-500/30"
              whileTap={{ scale: 0.95 }}
              data-testid="nav-play"
            >
              <Swords className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              <span className="text-[9px] sm:text-[10px] font-anton text-white uppercase">PVP</span>
            </motion.button>
            
            {/* Squid Game - NEW */}
            <motion.button
              onClick={goToSquidGame}
              className="flex flex-col items-center gap-0.5 px-3 sm:px-4 py-2 rounded-xl bg-gradient-to-t from-pink-600 to-red-500 shadow-lg shadow-pink-500/30"
              whileTap={{ scale: 0.95 }}
              data-testid="nav-squid"
            >
              <Skull className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              <span className="text-[9px] sm:text-[10px] font-anton text-white uppercase">BRIDGE</span>
            </motion.button>
            
            {/* Deck (Gallery) */}
            <motion.button
              onClick={goToGallery}
              className="flex flex-col items-center gap-0.5 px-2 sm:px-3 py-2 rounded-lg hover:bg-white/10 transition-colors"
              whileTap={{ scale: 0.95 }}
              data-testid="nav-deck"
            >
              <Images className="w-5 h-5 sm:w-6 sm:h-6 text-cyan-400" />
              <span className="text-[9px] sm:text-[10px] font-space text-white/70 uppercase">Deck</span>
            </motion.button>
            
            {/* Market */}
            <motion.button
              onClick={goToMarketplace}
              className="flex flex-col items-center gap-0.5 px-2 sm:px-3 py-2 rounded-lg hover:bg-white/10 transition-colors"
              whileTap={{ scale: 0.95 }}
              data-testid="nav-market"
            >
              <Store className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-400" />
              <span className="text-[9px] sm:text-[10px] font-space text-white/70 uppercase">Market</span>
            </motion.button>
          </div>
        </div>
      </nav>
      
      {/* Daily Reward Modal - First thing user sees once a day */}
      <DailyRewardModal
        isOpen={showDailyReward}
        onClose={closeDailyRewardModal}
        onClaim={claimDailyReward}
        reward={dailyReward}
        streak={dailyStreak}
        isClaiming={isClaimingDaily}
      />
      
      {/* Solana Wallet Manager Modal */}
      <SolanaWalletManager
        isOpen={showSolanaWallet}
        onClose={() => setShowSolanaWallet(false)}
        userId={sessionId}
      />
      
      {/* Onboarding Tutorial - For new users */}
      {showTutorial && (
        <OnboardingTutorial onComplete={() => setShowTutorial(false)} />
      )}

      {/* Admin Debug Menu - Hidden, Long Press Logo to Open */}
      <AdminDebugMenu
        isOpen={showAdminMenu}
        onClose={() => setShowAdminMenu(false)}
        onForceWin={() => {
          // Trigger victory animation
          toast.success('🏆 Victory!');
        }}
      />
    </div>
  );
};

export default Home;